import { AudioTrackId, AudioStream, getAudioStream } from '../../domain/models';
import { getAudioTrackDurationMs, getCountdownAudioTrackId } from '../../domain/logic';
import { IAudioService, PlayOptions } from '@breath-lab/services';
import { getAudioUrl } from './AudioTrackRegistry';

// ---------------------------------------------------------------------------
// Volume constants (mirrors AudioService in breathwork_kit)
// ---------------------------------------------------------------------------

const MUSIC_VOLUME = 0.7;
const VOICE_OVER_VOLUME = 1.0;
const BREATHING_VOLUME = 0.4;

// 1-sample silent WAV used to prime/unlock HTMLAudioElement on iOS WebKit.
// iOS requires .play() to be called on each element from inside a user gesture
// before it can play unattended; loading this silent clip and calling play
// satisfies that requirement without producing audible output.
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

// Breathing tracks that are quieter and need volume normalization
const QUIET_BREATHING_TRACKS = new Set<AudioTrackId>([
  AudioTrackId.exhaleSound02Sec,
  AudioTrackId.exhaleSound04Sec,
  AudioTrackId.exhaleSound06Sec,
  AudioTrackId.exhaleSound08Sec,
  AudioTrackId.exhaleSound12Sec,
  AudioTrackId.exhaleSound20Sec,
  AudioTrackId.exhaleSound30Sec,
  AudioTrackId.inhaleSound02Sec,
  AudioTrackId.inhaleSound04Sec,
  AudioTrackId.inhaleSound06Sec,
  AudioTrackId.inhaleSound08Sec,
  AudioTrackId.inhaleSound12Sec,
  AudioTrackId.inhaleSound20Sec,
  AudioTrackId.inhaleSound30Sec,
]);

const UJJAYI_BREATHING_TRACKS = new Set<AudioTrackId>([
  AudioTrackId.inhaleSoundUjjayi2Sec,
  AudioTrackId.inhaleSoundUjjayi4Sec,
  AudioTrackId.exhaleSoundUjjayi2Sec,
  AudioTrackId.exhaleSoundUjjayi4Sec,
]);

const BEE_BREATHING_TRACKS = new Set<AudioTrackId>([
  AudioTrackId.exhaleSoundBee15Sec,
  AudioTrackId.exhaleSoundBee30Sec,
]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function normalizedBreathingVolume(trackId: AudioTrackId, voiceOverActive: boolean): number {
  let base: number;
  if (QUIET_BREATHING_TRACKS.has(trackId)) {
    base = 0.1;
  } else if (UJJAYI_BREATHING_TRACKS.has(trackId) || BEE_BREATHING_TRACKS.has(trackId)) {
    base = 0.3;
  } else {
    base = 1.0;
  }
  // Reduce breathing volume when voice over is playing
  return voiceOverActive ? 0.7 * base : base;
}

function createSilentAudio(): HTMLAudioElement {
  const el = new Audio();
  el.preload = 'auto';
  return el;
}

// ---------------------------------------------------------------------------

/**
 * Browser implementation of IAudioService using HTMLAudioElement.
 *
 * Three channels:
 *   - music: looping background track
 *   - voiceOver: sequential spoken cues
 *   - breathing: speed-adjusted breathing sounds (rate = actualDuration / stepDuration)
 *
 * Port of AudioService in breathwork_kit/lib/services/audio_service.dart.
 */
export class WebAudioService implements IAudioService {
  // Persistent elements per channel. iOS WebKit unlocks audio per-element, not
  // per-page, so we MUST reuse the same element across plays and prime each one
  // inside a user gesture via unlock(). Recreating elements (the previous
  // approach) caused all audio after the first play to be silently blocked.
  private _music: HTMLAudioElement = createSilentAudio();
  private _voiceOver: HTMLAudioElement = createSilentAudio();
  private _breathing: HTMLAudioElement = createSilentAudio();

  private _isDisposed = false;
  private _isUnlocked = false;

  // Each safeCompleteCountdown() / new playCountdown() increments this version.
  // Any in-flight countdown loop exits as soon as it sees its version is stale.
  private _countdownVersion = 0;

  // ---------------------------------------------------------------------------
  // IAudioService — lifecycle
  // ---------------------------------------------------------------------------

  async initialize(): Promise<void> {
    // HTMLAudioElement requires no async setup; volumes are set per-play.
  }

  /**
   * Prime each channel's element by loading a silent clip and calling play()
   * synchronously inside a user gesture. After this returns, the elements
   * remain unlocked across src changes, so subsequent plays from timers or
   * promise chains succeed on iOS Safari/Chrome.
   *
   * MUST be called from a click/tap handler. Idempotent.
   */
  unlock(): void {
    if (this._isUnlocked || this._isDisposed) return;
    this._isUnlocked = true;
    for (const el of [this._music, this._voiceOver, this._breathing]) {
      // If a real track is already loaded, re-trigger play() in case the initial
      // autoplay attempt was blocked by browser policy — don't clobber the src.
      const hasRealSrc = el.src && !el.src.startsWith('data:');
      try {
        if (hasRealSrc) {
          el.play().catch(() => {/* silently ignore — element will resume on next play() call */});
        } else {
          el.muted = true;
          el.src = SILENT_WAV;
          el.loop = false;
          const p = el.play();
          if (p && typeof p.then === 'function') {
            p.then(() => {
              // Only clean up if no real playback has taken over this element
              // since we kicked off the silent clip.
              try {
                if (el.src === SILENT_WAV || el.currentSrc.startsWith('data:')) {
                  el.pause();
                  el.currentTime = 0;
                }
                el.muted = false;
              } catch {/* ignore */}
            }).catch(() => {/* unlock not granted; first play will retry */});
          } else {
            el.muted = false;
          }
        }
      } catch {
        // ignore — the channel will retry on first real play
      }
    }
  }

  async preloadTracks(trackIds: AudioTrackId[]): Promise<void> {
    // Kick off load requests so the browser can cache the files.
    for (const id of trackIds) {
      const url = getAudioUrl(id);
      if (!url) continue;
      const el = new Audio();
      el.preload = 'auto';
      el.src = url;
    }
  }

  dispose(): void {
    this._isDisposed = true;
    this.safeCompleteCountdown();
    this._releaseElement(this._music);
    this._releaseElement(this._voiceOver);
    this._releaseElement(this._breathing);
  }

  // ---------------------------------------------------------------------------
  // IAudioService — music
  // ---------------------------------------------------------------------------

  async playMusic(): Promise<void> {
    if (this._isDisposed) return;
    const url = getAudioUrl(AudioTrackId.meditationMusic);
    const el = this._music;
    if (el.src !== url) el.src = url;
    el.loop = true;
    el.volume = MUSIC_VOLUME;
    el.muted = false;
    try {
      el.currentTime = 0;
    } catch {/* element not yet ready */}
    await el.play().catch(() => {/* autoplay policy — silently skip */});
  }

  async stopMusic(): Promise<void> {
    this._stopElement(this._music);
  }

  // ---------------------------------------------------------------------------
  // IAudioService — track playback
  // ---------------------------------------------------------------------------

  async play(trackId: AudioTrackId, options: PlayOptions = {}): Promise<void> {
    if (this._isDisposed) return;
    const { enforcedDurationMs, awaitCompletion = true } = options;
    const url = getAudioUrl(trackId);
    if (!url) return;

    const stream = getAudioStream(trackId);

    switch (stream) {
      case AudioStream.breathing:
        await this._playBreathing(trackId, url, enforcedDurationMs, awaitCompletion);
        break;
      case AudioStream.voiceOver:
        await this._playVoiceOver(url, awaitCompletion);
        break;
      case AudioStream.music:
        // Route music through playMusic()
        await this.playMusic();
        break;
    }
  }

  stopVoiceOverAndBreathing(): void {
    this.safeCompleteCountdown();
    this._stopElement(this._voiceOver);
    this._stopElement(this._breathing);
  }

  async stopAll(): Promise<void> {
    this.stopVoiceOverAndBreathing();
    await this.stopMusic();
  }

  // ---------------------------------------------------------------------------
  // IAudioService — countdown
  // ---------------------------------------------------------------------------

  async playCountdown(from: number): Promise<void> {
    if (this._isDisposed || from <= 1) return;

    // Capture this run's version. safeCompleteCountdown() or a concurrent
    // playCountdown() call will increment _countdownVersion, causing this
    // loop to exit at its next version check — with no try/finally bookkeeping
    // needed and no race on _countdownActive.
    const version = ++this._countdownVersion;

    let current = from;

    while (current > 0) {
      if (this._countdownVersion !== version || this._isDisposed) break;

      const trackId = getCountdownAudioTrackId(current);
      const durationMs = trackId ? getAudioTrackDurationMs(trackId) : null;

      if (!trackId || !durationMs) {
        await this._interruptibleDelay(1000, version);
        current--;
        continue;
      }

      if (this._countdownVersion !== version || this._isDisposed) break;

      const url = getAudioUrl(trackId);
      const el = this._voiceOver;
      try { el.pause(); } catch {/* ignore */}
      if (el.src !== url) el.src = url;
      el.loop = false;
      el.volume = VOICE_OVER_VOLUME;
      el.muted = false;
      try { el.currentTime = 0; } catch {/* not yet loaded */}
      await el.play().catch(() => {});

      await this._interruptibleDelay(1000, version);
      if (this._countdownVersion !== version || this._isDisposed) break;

      const step = Math.ceil(durationMs / 1000);
      current -= step;
    }
  }

  safeCompleteCountdown(): void {
    this._countdownVersion++;
  }

  isVoiceOverPlaying(): boolean {
    const el = this._voiceOver;
    return !!el.src && !el.paused && !el.ended;
  }

  muteVoiceOver(muted: boolean): void {
    this._voiceOver.volume = muted ? 0 : VOICE_OVER_VOLUME;
  }

  muteBreathing(muted: boolean): void {
    this._breathing.volume = muted ? 0 : BREATHING_VOLUME;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private async _playVoiceOver(url: string, awaitCompletion: boolean): Promise<void> {
    if (this._isDisposed) return;
    const el = this._voiceOver;
    try { el.pause(); } catch {/* ignore */}
    if (el.src !== url) el.src = url;
    el.loop = false;
    el.volume = VOICE_OVER_VOLUME;
    el.muted = false;
    el.playbackRate = 1.0;
    try { el.currentTime = 0; } catch {/* not yet loaded */}

    try {
      await el.play();
    } catch {
      return;
    }

    if (!awaitCompletion) return;

    const trackDurationMs = el.duration * 1000 || 0;
    if (trackDurationMs > 0) {
      await this._waitForElement(el);
    }
  }

  private async _playBreathing(
    trackId: AudioTrackId,
    url: string,
    enforcedDurationMs: number | undefined,
    awaitCompletion: boolean,
  ): Promise<void> {
    if (this._isDisposed) return;

    const voiceOverEl = this._voiceOver;
    const voiceOverPlaying = !!voiceOverEl.src && !voiceOverEl.paused;
    const volume = normalizedBreathingVolume(trackId, voiceOverPlaying) * BREATHING_VOLUME;

    const el = this._breathing;
    try { el.pause(); } catch {/* ignore */}
    if (el.src !== url) el.src = url;
    // Never loop: breathing is stretched via playback rate instead (mirrors Dart's
    // AudioPlayerProxy._calculatePlaybackSpeed: rate = actualDuration / enforcedDuration).
    el.loop = false;
    el.volume = volume;
    el.muted = false;
    el.playbackRate = 1.0;
    try { el.currentTime = 0; } catch {/* not yet loaded */}

    // Adjust playback speed so the track fills exactly the step duration.
    if (enforcedDurationMs != null && enforcedDurationMs > 0) {
      const actualDurationMs = getAudioTrackDurationMs(trackId);
      if (actualDurationMs != null && actualDurationMs > 0) {
        el.playbackRate = actualDurationMs / enforcedDurationMs;
      }
    }

    try {
      await el.play();
    } catch {
      return;
    }

    if (!awaitCompletion) return;

    const durationToWait = enforcedDurationMs ?? (el.duration * 1000 || 0);
    if (durationToWait > 0) {
      await this._timedDelay(durationToWait);
    }

    this._stopElement(el);
  }

  private _stopElement(el: HTMLAudioElement): void {
    try {
      el.pause();
      el.currentTime = 0;
    } catch {
      // ignore
    }
  }

  private _releaseElement(el: HTMLAudioElement): void {
    try {
      el.pause();
      el.removeAttribute('src');
      el.load();
    } catch {
      // ignore
    }
  }

  private _waitForElement(el: HTMLAudioElement): Promise<void> {
    return new Promise((resolve) => {
      const onEnd = () => resolve();
      el.addEventListener('ended', onEnd, { once: true });
      el.addEventListener('error', onEnd, { once: true });
    });
  }

  private _timedDelay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /** A delay that resolves early when the countdown version changes or service is disposed. */
  private _interruptibleDelay(ms: number, countdownVersion?: number): Promise<void> {
    return new Promise((resolve) => {
      const id = setTimeout(resolve, ms);
      const check = setInterval(() => {
        if (this._isDisposed || (countdownVersion !== undefined && this._countdownVersion !== countdownVersion)) {
          clearTimeout(id);
          clearInterval(check);
          resolve();
        }
      }, 50);
      setTimeout(() => clearInterval(check), ms + 100);
    });
  }
}
